import React, { useEffect, forwardRef, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTourPackagesByType } from '../redux/slices/packageSlice';
import PackageCard from './Cards/PackagesCard';

const PackageSectionThrill = forwardRef(({ title, tourType, contactNumbers }, ref) => {
    const dispatch = useDispatch();
    const scrollContainerRef = useRef(null);
    const { domesticPackages, internationalPackages, customisedPackages, shorttripPackages, activitiesPackages, loading, error } = useSelector((state) => state.package);

    const titleToTourType = {
        'Domestic Tours': 'Domestic',
        'International Tours': 'International',
        'Customised Tours': 'Customised',
        'Short Trips': 'Short',
        'Activities': 'Activities',
    };

    const placeholderPackage = {
        id: 'placeholder',
        days: '',
        rating: 0,
        reviews: 0,
        title: '',
        images: ['https://images.unsplash.com/photo-1566873535350-a3f5d4a804b7?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'],
        itinerary: '',
        price: 0,
        originalPrice: 0,
        savings: 0,
    };

    useEffect(() => {
        if (tourType && titleToTourType[title]) {
            dispatch(fetchTourPackagesByType(tourType));
        }
    }, [dispatch, tourType]);

    let packagesToDisplay = [];
    if (tourType === 'Activities') {
        packagesToDisplay = activitiesPackages;
    } else if (tourType === 'Domestic') {
        packagesToDisplay = domesticPackages;
    } else if (tourType === 'International') {
        packagesToDisplay = internationalPackages;
    } else if (tourType === 'Customised') {
        packagesToDisplay = customisedPackages;
    } else if (tourType === 'Short') {
        packagesToDisplay = shorttripPackages;
    }

    const displayPackages = loading
        ? Array(3).fill(placeholderPackage).map((pkg, index) => ({ ...pkg, id: `placeholder-${index}` }))
        : packagesToDisplay;

    const sectionId = tourType === 'Activities' ? 'Activities' : (titleToTourType[title] || title.toLowerCase().replace(/\s+/g, ''));

    const handlePrevImage = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
        }
    };

    const handleNextImage = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
        }
    };

    const buttonBaseStyle = {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10,
        backgroundColor: 'transparent',
        borderRadius: '9999px',
        padding: '8px sm:12px',
        transition: 'background-color 0.3s ease',
    };

    return (
        <div className="mb-6 sm:mb-8" ref={ref}>
            <div className="flex justify-between items-center mb-3 sm:mb-4 relative">
                <h2 className="text-xl sm:text-2xl font-semibold text-custom-blue mx-auto">
                    {tourType === 'Activities' ? 'Activities' : title}
                </h2>
            </div>

            {error && <div className="text-center text-red-600 text-sm sm:text-base">Error: {error}</div>}
            {!loading && !error && packagesToDisplay.length === 0 && (
                <div className="text-center text-gray-600 text-sm sm:text-base">
                    No {tourType === 'Activities' ? 'Activities' : title.toLowerCase()} available at the moment.
                </div>
            )}

            <div className="relative">
                <button
                    style={{ ...buttonBaseStyle, left: '-40px' }}
                    className="hidden sm:block"
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.4)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    onClick={(e) => {
                        e.preventDefault();
                        handlePrevImage();
                    }}
                >
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                <div
                    ref={scrollContainerRef}
                    className="flex space-x-1 sm:space-x-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory"
                    style={{ filter: loading ? 'blur(4px)' : 'none', transition: 'filter 0.3s ease' }}
                >
                    {displayPackages.map((pkg) => (
                        <div key={pkg._id || pkg.id} className="snap-start">
                            <PackageCard pkg={pkg} contactNumbers={contactNumbers} />
                        </div>
                    ))}
                </div>

                <button
                    style={{ ...buttonBaseStyle, right: '-40px' }}
                    className="hidden sm:block"
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.4)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    onClick={(e) => {
                        e.preventDefault();
                        handleNextImage();
                    }}
                >
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </div>
    );
});

export default PackageSectionThrill;